import { useMemo } from "react";

import { AccountType } from "@/auth/types/LoginAuth";

/**
 * Hook to check user permissions
 * Reads permissions from localStorage user profile
 */
export const usePermissions = () => {
  // Get user data from localStorage
  const userData = useMemo(() => {
    try {
      const userString = localStorage.getItem("user");
      if (userString) {
        return JSON.parse(userString);
      }
    } catch {
      return null;
    }
    return null;
  }, []);

  // Get permissions from user data
  const permissions = useMemo(() => {
    return userData?.permissions || [];
  }, [userData]);

  // Get account type from user data
  const accountType = useMemo((): AccountType | null => {
    return userData?.account_type || null;
  }, [userData]);

  /**
   * Check if user is a super admin
   * @returns boolean - true if user has SUPER_ADMIN account type
   */
  const isSuperAdmin = useMemo(() => {
    return accountType === "SUPER_ADMIN";
  }, [accountType]);

  /**
   * Check if user has a specific permission
   * @param permission - Permission string in format "module:action" (e.g., "employees:view")
   * @returns boolean - true if user has the permission
   */
  const hasPermission = (permission: string): boolean => {
    if (!permissions || !Array.isArray(permissions)) {
      return false;
    }
    return permissions.includes(permission);
  };

  /**
   * Check if user has any of the specified permissions
   * @param permissionList - Array of permission strings
   * @returns boolean - true if user has at least one of the permissions
   */
  const hasAnyPermission = (permissionList: string[]): boolean => {
    if (!permissions || !Array.isArray(permissions)) {
      return false;
    }
    return permissionList.some((permission) =>
      permissions.includes(permission),
    );
  };

  /**
   * Check if user has all of the specified permissions
   * @param permissionList - Array of permission strings
   * @returns boolean - true if user has all of the permissions
   */
  const hasAllPermissions = (permissionList: string[]): boolean => {
    if (!permissions || !Array.isArray(permissions)) {
      return false;
    }
    return permissionList.every((permission) =>
      permissions.includes(permission),
    );
  };

  return {
    permissions,
    accountType,
    isSuperAdmin,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
};
